import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';
import has from 'lodash/has';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MuiMenuItem from '@material-ui/core/MenuItem';
import { useTranslation } from 'utils/modifiedTranslation';

import useStyles from './styles';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

const MenuItem = React.forwardRef((props, ref) => <MuiMenuItem {...props} ref={ref} />);

/**
 * A single-select input for choosing one option
 */
const SingleSelect = props => {
  const { id, placeholder, options, values, rootClassName, onChange, valueToScrollIntoView } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const scrollToRef = React.useRef(null);
  const enableScrollBehavior = isEmpty(values) && valueToScrollIntoView;

  const handleChange = event => {
    onChange([
      {
        label: event.currentTarget.innerText,
        value: event.target.value
      }
    ]);
  };

  // Check if value is equal to [{value: any}]
  const value = some(values, val => has(val, 'value')) ? values[0].value : '';

  const onOpen = () => {
    if (enableScrollBehavior) {
      setTimeout(() => {
        if (scrollToRef.current) {
          scrollToRef.current.scrollIntoView();
        }
      }, 1);
    }
  };

  return (
    <FormControl variant="outlined" className={`${classes.formControl} ${rootClassName}`}>
      {!values.length && <InputLabel id={`${id}-outlined-single-select`}>{t(placeholder)}</InputLabel>}

      <Select
        id={id}
        labelId={`${id}-outlined-single-select`}
        value={value}
        onChange={handleChange}
        label={t(placeholder)}
        className={classes.select}
        MenuProps={MenuProps}
        onOpen={onOpen}
        onClose={onOpen}
      >
        {options.map((option, index) => {
          const storeRef = !!(enableScrollBehavior && valueToScrollIntoView === option.value && !scrollToRef.current);
          return (
            <MenuItem
              key={`${option.value}-${index}`}
              value={option.value}
              className={classes.listItem}
              ref={ref => (storeRef ? (scrollToRef.current = ref) : ref)}
            >
              {t(option.label)}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

SingleSelect.propTypes = {
  /**
   * Unique identifier for select
   */
  id: PropTypes.string.isRequired,
  /**
   * Placeholder of the input.
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * Array of options for the dropdown list
   */
  options: PropTypes.array.isRequired,
  /**
   * Array of selected options. Array of 1 object in which presents label and value of chosen options.
   */
  values: PropTypes.array,
  /**
   * Classname for the root div
   */
  rootClassName: PropTypes.string,
  /**
   * Function to call when option selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * The value to scroll to in the menu list. Will only work if values props is empty
   */
  valueToScrollIntoView: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

SingleSelect.defaultProps = {
  values: [],
  rootClassName: ''
};

export default SingleSelect;
